import { createSessionStoragePersister } from '@client/utils/data/zustand';
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { isPersistableGridStateKey } from './persistable-grid-state';

export interface PathStateValue {
    value: unknown;
    pathname: string;
}

export interface PathStateStore {
    states: Record<string, PathStateValue>;
    setState: (key: string, value: unknown, pathname: string) => void;
    getState: (key: string) => PathStateValue | undefined;
    deleteState: (key: string) => void;
    cleanupStatesForNavigation: (currentPath: string) => void;
}

interface PersistedPathState {
    states: Record<string, PathStateValue>;
}

/**
 * Path state also holds transient UI state, so reload persistence is deliberately limited to
 * table query, filter, search and view keys. This keeps dialogs and drawers session-local while
 * preserving the grid state covered by CUR-615.
 */
function pickPersistableGridStates(states: Record<string, PathStateValue>): Record<string, PathStateValue> {
    return Object.fromEntries(Object.entries(states).filter(([key]) => isPersistableGridStateKey(key)));
}

/**
 * Drops anything that doesn't look like a `PathStateValue` record. Session storage survives
 * deploys, so a hydrated envelope may come from an older build (or be tampered with) — a bad
 * entry must degrade to "no stored state", never crash hydration or the per-navigation cleanup.
 * Keys are restricted to persistable grid state, which also keeps transient UI state and hostile
 * keys like `__proto__` out of the rebuilt maps.
 */
function sanitizePersistedStates(persisted: unknown): Record<string, PathStateValue> {
    const states = (persisted as Partial<PersistedPathState> | null | undefined)?.states;
    if (states === null || typeof states !== 'object') {
        return {};
    }
    const sanitized: Record<string, PathStateValue> = {};
    for (const [key, entry] of Object.entries(states)) {
        if (!isPersistableGridStateKey(key)) continue;
        if (entry === null || typeof entry !== 'object') continue;
        const pathname = (entry as PathStateValue).pathname;
        // An absolute pathname is required: shouldDeleteState treats '' as an ancestor of every
        // route, so an empty/relative pathname would make the entry immune to all pruning.
        if (typeof pathname === 'string' && pathname.startsWith('/')) {
            sanitized[key] = entry as PathStateValue;
        }
    }
    return sanitized;
}

export const usePathStateStore = create<PathStateStore>()(
    subscribeWithSelector(
        persist(
            (set, get) => ({
                states: {},

                setState: (key: string, value: unknown, pathname: string) => {
                    set((state) => ({
                        states: {
                            ...state.states,
                            [key]: { value, pathname },
                        },
                    }));
                },

                getState: (key: string) => {
                    return get().states[key];
                },

                deleteState: (key: string) => {
                    set((state) => {
                        const { [key]: deleted, ...rest } = state.states;
                        void deleted; // Mark as intentionally unused
                        return { states: rest };
                    });
                },

                cleanupStatesForNavigation: (currentPath: string) => {
                    set((state) => {
                        const newStates: Record<string, PathStateValue> = {};

                        Object.entries(state.states).forEach(([key, stateValue]) => {
                            const keyPath = stateValue.pathname;

                            // Determine if this key should be deleted based on navigation rules
                            if (!shouldDeleteState(keyPath, currentPath)) {
                                newStates[key] = stateValue;
                            }
                        });

                        return { states: newStates };
                    });
                },
            }),
            {
                name: 'path-state',
                version: 2, // CUR-1004: drop where clauses built with the pre-rename relation key.
                // Older-version envelopes are discarded outright (grid state is cheap to lose,
                // wrong-shaped state is not). Explicit so a version bump never logs a
                // missing-migration error; the rehydrate cleanup then rewrites storage clean.
                migrate: () => ({ states: {} }),
                // Session-scoped (CUR-615): grid state survives full reloads within the tab —
                // including the silent SPA reload every deploy triggers — and clears when the
                // tab is closed.
                storage: createSessionStoragePersister<PersistedPathState>(),
                partialize: (state) => ({ states: pickPersistableGridStates(state.states) }),
                merge: (persisted, current) => ({ ...current, states: sanitizePersistedStates(persisted) }),
                // Rehydration runs synchronously at store creation (sync storage), before any
                // route component reads path state. Pruning against the initial pathname applies
                // the same navigation rules a live session applies on every route change, so a
                // reload restores exactly what an in-app return would have kept — and state from
                // other modules is dropped (the cross-module reset rule survives reloads).
                onRehydrateStorage: () => (state) => {
                    if (state && typeof window !== 'undefined') {
                        state.cleanupStatesForNavigation(window.location.pathname);
                    }
                },
            },
        ),
    ),
);

/**
 * Determines if a state should be deleted based on navigation rules
 * @param statePath The path associated with the stored state
 * @param currentPath The current navigation path
 * @returns true if the state should be deleted, false otherwise
 *
 * Rules:
 * - Keep states in the current path hierarchy (current path and its parents)
 * - Delete child states when navigating to a parent path
 * - Delete all states from old path hierarchy when navigating to a sibling path
 */
function shouldDeleteState(statePath: string, currentPath: string): boolean {
    // Don't delete if it's the current path
    if (statePath === currentPath) {
        return false;
    }

    // Don't delete if the state path is a parent of the current path
    // (e.g., when at /incidents/details, keep /incidents state)
    if (currentPath.startsWith(statePath) && currentPath[statePath.length] === '/') {
        return false;
    }

    // If state path is a child of current path, check if we should keep it
    if (statePath.startsWith(currentPath) && statePath[currentPath.length] === '/') {
        // When navigating to parent, delete child states
        // (e.g., when navigating from /incidents/details to /incidents, delete /incidents/details)
        return true;
    }

    // Delete all states that are not in the current path hierarchy
    // (e.g., when navigating from /incidents to /complaints, delete all /incidents states)
    return true;
}
